import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Google as GoogleIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const { login, isLoading, error } = useAuth();

  const handleGoogleLogin = () => {
    login();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 3
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'center'
          }}
        >
          {/* Logo/Brand */}
          <Box sx={{ mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                fontSize: '2rem',
                fontWeight: 700
              }}
            >
              A
            </Avatar>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              UniSign
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Gerencie suas assinaturas de email de forma unificada
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                textAlign: 'left'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Bem-vindo de volta!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Faça login para acessar sua conta e gerenciar suas assinaturas
            </Typography>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GoogleIcon />
                )
              }
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                  transform: 'none'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isLoading ? 'Entrando...' : 'Entrar com Google'}
            </Button>
          </Box>

          {/* Features */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 3,
            mt: 4,
            pt: 4,
            borderTop: '1px solid #e2e8f0'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: 'success.main'
                }}
              >
                <EmailIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Assinaturas Inteligentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crie e gerencie assinaturas personalizadas para diferentes domínios
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: 'info.main'
                }}
              >
                <EmailIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Sincronização Automática
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sincronize automaticamente com Gmail e Zoho Desk
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary">
              © 2025 UniSign. Todos os direitos reservados.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
